import { Routes, Route, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";

// Pages
import LandingPage from "./pages/LandingPage";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Workspaces from "./pages/Workspaces";
import Projects from "./pages/Projects";
import Tests from "./pages/Tests";
import TestEditor from "./pages/TestEditor";
import ApiTestEditor from "./pages/ApiTestEditor";
import Suites from "./pages/Suites";
import SuiteExecutions from "./pages/SuiteExecutions";
import DataSets from "./pages/DataSets";
import Executions from "./pages/Executions";
import Analytics from "./pages/Analytics";
import Settings from "./pages/Settings";
import Help from "./pages/Help";
import PlatformTenants from "./pages/PlatformTenants";
import { isPlatformToken } from "./utils/session";

// Components
import Layout from "./components/Layout";

// Protected route wrapper. Platform operators see only the platform pages
// (platform), organisation users never do; the backend enforces the same.
const ProtectedRoute = ({ children, platform = false }) => {
	const { isAuthenticated, token } = useSelector((state) => state.auth);

	if (!isAuthenticated) {
		return <Navigate to="/login" replace />;
	}
	const platformSession = isPlatformToken(token);
	if (platform !== platformSession) {
		return <Navigate to={platformSession ? "/platform" : "/dashboard"} replace />;
	}

	return <Layout>{children}</Layout>;
};

// Public route wrapper (redirect to the home page if already logged in)
const PublicRoute = ({ children }) => {
	const { isAuthenticated, token } = useSelector((state) => state.auth);

	if (isAuthenticated) {
		return <Navigate to={isPlatformToken(token) ? "/platform" : "/dashboard"} replace />;
	}

	return children;
};

function App() {
	return (
		<Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
			<AnimatePresence mode="wait">
				<Routes>
					{/* Public Routes */}
					<Route
						path="/login"
						element={
							<PublicRoute>
								<Login />
							</PublicRoute>
						}
					/>
					<Route path="/reset-password" element={<ResetPassword />} />

					{/* Protected Routes */}
					<Route
						path="/dashboard"
						element={
							<ProtectedRoute>
								<Dashboard />
							</ProtectedRoute>
						}
					/>

					<Route
						path="/workspaces"
						element={
							<ProtectedRoute>
								<Workspaces />
							</ProtectedRoute>
						}
					/>

					<Route
						path="/projects"
						element={
							<ProtectedRoute>
								<Projects />
							</ProtectedRoute>
						}
					/>

					<Route
						path="/tests"
						element={
							<ProtectedRoute>
								<Tests key="ui" mode="ui" />
							</ProtectedRoute>
						}
					/>

					{/* API tests: the same page, listing only them (keyed, so switching reloads) */}
					<Route
						path="/apis"
						element={
							<ProtectedRoute>
								<Tests key="api" mode="api" />
							</ProtectedRoute>
						}
					/>

					<Route
						path="/tests/new"
						element={
							<ProtectedRoute>
								<TestEditor />
							</ProtectedRoute>
						}
					/>

					<Route
						path="/tests/:id"
						element={
							<ProtectedRoute>
								<TestEditor />
							</ProtectedRoute>
						}
					/>

					<Route path="/tests/:id/edit" />

					<Route
						path="/api-tests/new"
						element={
							<ProtectedRoute>
								<ApiTestEditor />
							</ProtectedRoute>
						}
					/>

					<Route
						path="/api-tests/:id"
						element={
							<ProtectedRoute>
								<ApiTestEditor />
							</ProtectedRoute>
						}
					/>

					<Route
						path="/suites"
						element={
							<ProtectedRoute>
								<Suites />
							</ProtectedRoute>
						}
					/>

					<Route
						path="/suite-executions"
						element={
							<ProtectedRoute>
								<SuiteExecutions />
							</ProtectedRoute>
						}
					/>

					<Route
						path="/datasets"
						element={
							<ProtectedRoute>
								<DataSets />
							</ProtectedRoute>
						}
					/>

					<Route
						path="/executions"
						element={
							<ProtectedRoute>
								<Executions />
							</ProtectedRoute>
						}
					/>

					<Route
						path="/analytics"
						element={
							<ProtectedRoute>
								<Analytics />
							</ProtectedRoute>
						}
					/>

					<Route
						path="/settings"
						element={
							<ProtectedRoute>
								<Settings />
							</ProtectedRoute>
						}
					/>

					<Route
						path="/platform"
						element={
							<ProtectedRoute platform>
								<PlatformTenants />
							</ProtectedRoute>
						}
					/>

					<Route
						path="/help"
						element={
							<ProtectedRoute>
								<Help />
							</ProtectedRoute>
						}
					/>

					{/* Landing page */}
					<Route path="/" element={<LandingPage />} />
					<Route path="/contact" element={<Contact />} />
					<Route path="/privacy" element={<Privacy />} />

					{/* Catch-all */}
					<Route
						path="*"
						element={<Navigate to="/dashboard" replace />}
					/>
				</Routes>
			</AnimatePresence>
		</Box>
	);
}

export default App;
